<template>
  <v-sheet style="margin-top: 2em; margin-left: 2em" width="200">
    <v-form @submit.prevent="getArticles">
      <v-text-field
        clearable
        type="date"
        v-model="date"
        label="Date"
        density="compact"
        variant="outlined"
      />
      <v-combobox
        label="Point de vente"
        :items="itemsEtabs"
        v-model="store.state.etablissement"
        variant="outlined"
        density="compact"
      />

      <barcodeScanner />
      <h6>{{ store.state.scanBarCode }}</h6>

      <v-text-field
        clearable
        v-model="filtreParams.reference"
        label="Article"
        variant="outlined"
        density="compact"
        @change="store.commit('addBarcode', $event.target.value)"
      />

      <v-combobox clearable chips multiple label="marques" :items="store.state.marques"
                  v-model="filtreParams.code_marque" variant="outlined" density="compact" />
      <v-combobox clearable chips multiple label="Thèmes" :items="itemsThemes"
                  v-model="filtreParams.code_theme" variant="outlined" density="compact" />
      <v-combobox clearable chips multiple label="Rayons" :items="itemsRayons"
                  v-model="filtreParams.code_rayon" variant="outlined" density="compact" />
      <v-combobox clearable chips multiple label="Familles" :items="itemsFamilles"
                  v-model="filtreParams.code_famille" variant="outlined" density="compact" />
      <v-combobox clearable chips multiple label="SousFamilles" :items="itemsSousFamilles"
                  v-model="filtreParams.code_sousfamille" variant="outlined" density="compact" />
      <v-combobox clearable chips multiple label="Collections" :items="itemsCollections"
                  v-model="filtreParams.collection" variant="outlined" density="compact" />
      <v-combobox clearable chips multiple label="promo" :items="TYPES_PROMO"
                  v-model="filtreParams.type_promo" variant="outlined" density="compact"
                  @update:model-value="setPromo" />

      <v-checkbox label="Stock?" style="margin-top: -10px" v-model="filtreParams.enStock" density="compact" />
      <v-checkbox label="MonStock?" style="margin-top: -30px" v-model="filtreParams.monStock" density="compact"
                  @update:model-value="v => { if (v) filtreParams.enStock = true }" />
      <v-checkbox label="Promo?" style="margin-top: -30px" v-model="filtreParams.promo" density="compact"
                  @update:model-value="v => { if (!v) filtreParams.type_promo = null }" />

      <v-btn class="mt-2" block type="submit" :loading="chargement">Submit</v-btn>
      <v-overlay v-model="store.state.waiting" />
    </v-form>
  </v-sheet>

  <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="majEnCours ? -1 : 3000" location="top">
    <v-progress-linear v-if="majEnCours" :model-value="progression" striped height="15" color="deep-orange">
      <template #default="{ value }"><span>{{ Math.round(value) }}%</span></template>
    </v-progress-linear>
    <div>{{ snackbarMessage }}</div>
    <template v-if="!majEnCours" #actions>
      <v-btn color="white" variant="text" @click="snackbar = false">Fermer</v-btn>
    </template>
  </v-snackbar>
</template>

<script setup>
import { onMounted, computed, ref } from 'vue'
import axios from 'axios'
import store from './store'
import barcodeScanner from './barcodeScanner.vue'
import router from './router'
// File d'attente Cegid commune avec CodeArticle.vue (10 appels simultanés au maximum pour toute la page)
import { appelY2, decouper, etabPrix, LIMITE_PRIX, LIMITE_STOCK } from './outils/cegid'

// ------------------------------------------------------------ Constantes ---
const API_ARTICLES = 'https://my.edisac.com/goapi/articlesdeep'

const TYPES_PROMO = ['PBG', 'OUTLET', 'BF', 'SOLD1', 'SOLD2', 'SOLD3', 'SOLVP', 'BAG', 'PGEUSS', 'BRADER']
const itemsCollections = ['E23', 'E24', 'E25', 'E26', 'E27', 'H23', 'H24', 'H25', 'H26', 'H27', 'R23', 'R24', 'R25', 'R26', 'R27']
const itemsEtabs = ['DK03', 'MN01', 'EN11', 'FT04', 'LI02', 'RO06', 'AR09', 'CO18', 'HB21', 'BR08', 'NP10', 'GE22', 'OO10',
  'CA24', 'VX05', 'PR25', 'LO07', 'WEDI', 'WENL', 'WEBE', 'WOOM', 'WETR', 'WCAM', 'DEPO', 'DWEB', 'NEGO', 'NGEXT',
  'SHOPR', 'PHOTO', 'RECEP', 'DEFEC']

// ---------------------------------------------------------------- État ---
const filtreParams = ref({
  code_marque: null,
  code_theme: null,
  code_rayon: null,
  code_famille: null,
  code_sousfamille: null,
  reference: null,
  collection: null,
  type_promo: null,
  promo: null,
  enStock: true,
  monStock: null,
})
const chargement = ref(false)      // recherche des articles en cours
const majEnCours = ref(false)      // mise à jour prix/stock en cours
const progression = ref(0)

// Date : passe par la mutation au lieu de modifier store.state directement
const date = computed({
  get: () => store.state.date,
  set: v => store.commit('setDate', v),
})
const aujourdhui = () => new Date().toLocaleDateString('sv-SE') // AAAA-MM-JJ en heure locale

// ------------------------------------------------------------ Snackbar ---
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')
function showSnackbar(message, color = 'success') {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}

// ------------------------------------------------- Listes des filtres ---
/** "731 - ETRIER" ou { code: "731 - ETRIER" } -> "731" */
const codeDe = item => String(item?.code ?? item ?? '').split('-')[0].trim()

const codesMarques = computed(() => (filtreParams.value.code_marque || []).map(codeDe).filter(Boolean))

/**
 * Liste sans doublon à partir des lignes "a|b|c…" du store.
 * colMarque : colonne qui contient le code marque ; format : construit le libellé affiché.
 */
function listeFiltree(source, colMarque, format) {
  const marques = codesMarques.value
  const res = new Set()
  for (const ligne of source || []) {
    const p = String(ligne).split('|').map(s => s.trim())
    if (marques.length && !marques.includes(p[colMarque])) continue
    const libelle = format(p)
    if (libelle) res.add(libelle)
  }
  return [...res]
}
const deux = (a, b) => (a && b ? `${a} - ${b}` : '')

const itemsThemes = computed(() => listeFiltree(store.state.themes, 1, p => p[0]))
const itemsRayons = computed(() => listeFiltree(store.state.rayons, 0, p => deux(p[1], p[2])))
const itemsFamilles = computed(() => listeFiltree(store.state.familles, 0, p => deux(p[2], p[3])))
const itemsSousFamilles = computed(() => listeFiltree(store.state.sousFamilles, 0, p => deux(p[3], p[4])))

function setPromo() {
  filtreParams.value.promo = filtreParams.value.type_promo?.length ? true : null
}

// --------------------------------------------------- Recherche articles ---
/** ["731 - ETRIER", "812 - X"] -> "{731,812}" (tableau PostgreSQL) */
const versListe = v => (v?.length ? `{${v.map(codeDe)}}` : null)

async function getArticles() {
  if (chargement.value) return // évite deux recherches simultanées
  chargement.value = true
  store.commit('updateWaiting')
  const f = filtreParams.value

  try {
    const { data } = await axios.get(API_ARTICLES, {
      headers: { Authorization: `Bearer ${store.state.user.token}` },
      params: {
        reference: f.reference?.trim().toUpperCase() || null,
        marques: versListe(f.code_marque),
        themes: versListe(f.code_theme),
        rayons: versListe(f.code_rayon),
        familles: versListe(f.code_famille),
        sousfamilles: versListe(f.code_sousfamille),
        collections: f.collection?.length ? f.collection : null,
        type_promo: f.type_promo?.length ? f.type_promo : null,
        promo: f.promo || null,
        depot: store.state.etablissement,
        enStock: f.enStock || null,
        monStock: f.monStock || null,
      },
    })

    await router.replace({ path: '/edisac' })
    store.state.mode = ''
    store.commit('addArticles', data.articles)
    store.state.currentPage = 1
    store.state.itemsPerPage = 100
    store.commit('setArticlesBackUp', structuredClone(data.articles))
    store.commit('updateComponent', router.currentRoute.value.name)

    rafraichirPrixEtStock(etabPrix(store.state.etablissement), store.state.date) // sans await : la liste s'affiche, les prix arrivent ensuite
  } catch (error) {
    console.error('getArticles :', error.response?.status, error.message)
    showSnackbar(
      error.response?.status === 401 ? 'Session expirée, reconnectez-vous' : 'Erreur lors du chargement des articles',
      'error'
    )
  } finally {
    store.commit('updateWaiting')
    chargement.value = false
  }
}

// ------------------------------------------------ Prix et stock Cegid ---
/** codebarre -> position de l'article dans store.state.articles[marque][theme][reference][codebarre] */
function indexerArticles() {
  const index = new Map()
  for (const marque in store.state.articles)
    for (const theme in store.state.articles[marque])
      for (const reference in store.state.articles[marque][theme])
        for (const barcode in store.state.articles[marque][theme][reference])
          index.set(barcode, { marque, theme, reference })
  return index
}
const articleDe = (pos, barcode) => store.state.articles[pos.marque]?.[pos.theme]?.[pos.reference]?.[barcode]

async function rafraichirPrixEtStock(etablissement, dateJour) {
  const index = indexerArticles()
  const codes = [...index.keys()]
  if (!codes.length) return

  const lotsPrix = decouper(codes, LIMITE_PRIX)
  const lotsStock = decouper(codes, LIMITE_STOCK)
  const lotsRepart = decouper(codes, LIMITE_STOCK) // inventory-quantities : même limite de 21
  const total = lotsPrix.length + lotsStock.length + lotsRepart.length
  let faits = 0
  let erreurs = 0
  const avancer = () => { progression.value = (100 * ++faits) / total }

  const sub = { promo: new Set(), collec: new Set(), couleur: new Set(), taille: new Set() }

  progression.value = 0
  majEnCours.value = true
  showSnackbar('Mise à jour des prix, stock et répartition en cours…', 'warning')

  // Prix
  // appelY2 passe par la file commune : on peut lancer tous les lots, ils partent 10 par 10
  const prix = Promise.all(lotsPrix.map(async lot => {
    try {
      const { data } = await appelY2(`/stores/${etablissement}/selling-prices/v1`, {
        taxExcluded: false, date: dateJour, barcodes: lot,
      })
      for (const p of data?.prices ?? []) {
        const pos = index.get(p.barcode)
        if (!pos) continue
        const enPromo = p.unitPrice.base !== p.unitPrice.current
        store.commit('updateArticlePrice', {
          marque: pos.marque, theme: pos.theme, reference: pos.reference, barcode: p.barcode,
          vte_euro: p.unitPrice.base.toFixed(2),
          prix_promo: enPromo ? p.unitPrice.current.toFixed(2) : 0,
          type_promo: enPromo ? p.periodId : '',
        })
        const a = articleDe(pos, p.barcode)
        if (enPromo && p.periodId) sub.promo.add(p.periodId)
        if (a?.collection) sub.collec.add(a.collection)
        if (a?.code_couleur) sub.couleur.add(a.code_couleur)
        if (a?.taille) sub.taille.add(a.taille)
      }
    } catch (error) {
      erreurs++
      console.error('selling-prices : lot en échec', error.response?.status, error.message)
    } finally {
      avancer()
    }
  }))

  // Stock
  const stock = Promise.all(lotsStock.map(async lot => {
    try {
      const { data } = await appelY2('/available-quantities/v1', { barcodes: lot })
      for (const entry of Object.values(data ?? {})) {
        const pos = index.get(entry.barcode)
        if (!pos) continue
        const qtes = {}
        for (const s of entry.stores ?? []) qtes[s.id] = s.physicalQty
        store.commit('updateArticleStock', { ...pos, barcode: entry.barcode, stock: qtes })
      }
    } catch (error) {
      erreurs++
      console.error('available-quantities : lot en échec', error.response?.status, error.message)
    } finally {
      avancer()
    }
  }))

  // Répartition (minimum de réassort par magasin) de tous les articles trouvés
  const repartition = Promise.all(lotsRepart.map(async lot => {
    try {
      const { data } = await appelY2('/inventory-quantities/v1', { fields: 'Replenishment', barcodes: lot })
      for (const entry of data ?? []) {
        const pos = index.get(entry.barcode)
        if (!pos) continue
        const repart = {}
        for (const s of entry.stores ?? []) {
          const mini = s.warehouses?.[0]?.replenishment?.minimumInventoryQuantity
          if (mini) repart[s.id] = mini
        }
        store.commit('updateArticleRepartition', { ...pos, barcode: entry.barcode, repart })
      }
    } catch (error) {
      erreurs++
      console.error('inventory-quantities (répartition) : lot en échec', error.response?.status, error.message)
    } finally {
      avancer()
    }
  }))

  await Promise.all([prix, stock, repartition])

  store.commit('setSubTypeMap', {
    promo: [...sub.promo],
    collec: [...sub.collec],
    couleur: [...sub.couleur],
    taille: [...sub.taille],
  })
  majEnCours.value = false
  showSnackbar(
    erreurs ? `Prix et stock mis à jour, ${erreurs} appel(s) en erreur` : 'Mise à jour des prix et stock réussie !',
    erreurs ? 'warning' : 'success'
  )
}

onMounted(() => {
  date.value = aujourdhui()
})
</script>